import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import Button from '../UI/Button';

export type ModalProps = {
  isOpen: boolean;
  title: React.ReactNode;
  onClose(): void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
  titleAlign?: 'left' | 'center';
  disableInitialFocus?: boolean;
};

const widthClasses = {
  sm: 'w-80',
  md: 'w-96',
  lg: 'w-[32rem]',
  xl: 'w-[40rem]',
};

const focusableSelector =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  onClose,
  size = 'md',
  children,
  footer,
  showCloseButton = true,
  titleAlign = 'left',
  disableInitialFocus = false,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setActive(false);
    const timer = setTimeout(() => setActive(true), 10);
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      if (e.key === 'Tab') {
        if (!dialogRef.current) return;
        const nodes =
          dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector);
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    if (!disableInitialFocus) {
      const firstFocusable =
        dialogRef.current?.querySelector<HTMLElement>(focusableSelector);
      firstFocusable?.focus();
    }
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose, disableInitialFocus]);

  if (typeof document === 'undefined') return null;
  const root = document.getElementById('modal-root');
  if (!isOpen || !root) return null;

  const defaultFooter = (
    <div className='px-6 py-4 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2'>
      <Button
        variant='secondary'
        onClick={onClose}
        iconLeft={null}
        iconRight={null}
        className=''
      >
        Abbrechen
      </Button>
      <Button
        variant='primary'
        onClick={onClose}
        iconLeft={null}
        iconRight={null}
        className=''
      >
        OK
      </Button>
    </div>
  );

  return ReactDOM.createPortal(
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div
        className='fixed inset-0 bg-black/50 transition-opacity'
        aria-hidden='true'
      />
      <div
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby='modal-title'
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-modal ${widthClasses[size]} transform transition-all duration-150 ease-out ${active ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
      >
        <div className='flex items-start px-6 py-4'>
          <h3
            id='modal-title'
            className={`flex-grow text-lg font-semibold text-gray-900 dark:text-gray-50 ${titleAlign === 'center' ? 'text-center' : ''}`}
          >
            {title}
          </h3>
          {showCloseButton && (
            <Button
              variant='tertiary'
              onClick={onClose}
              iconLeft={null}
              iconRight={null}
              className=''
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='w-6 h-6'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                aria-label='Close'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </Button>
          )}
        </div>
        <div className='px-6 py-4 max-h-[80vh] overflow-y-auto'>{children}</div>
        {footer ?? defaultFooter}
      </div>
    </div>,
    root
  );
};

export default Modal;
